/**
 * Pesticide catalog freshness — API client + TanStack Query hook (AK-385 addendum).
 *
 * Surfaces the catalog-level sync metadata (edition of the applied Госкаталог
 * dump + when we synced + counts) so the UI can show the user «данные на
 * {edition}, синхронизировано {synced_at}» and warn the admin when a newer
 * edition is available on the Минсельхоз portal.
 *
 * The backend endpoint (GET /api/v1/reference/pesticides/sync-status) does a
 * best-effort probe of opendata.mcx.ru for the latest edition; when the portal
 * is unreachable `latestAvailableEdition` is null and `isStale` is false — the
 * UI degrades gracefully (shows the edition we have, no alarming alert).
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

/** Wire shape of GET /reference/pesticides/sync-status (snake_case from FastAPI). */
interface SyncStatusResponse {
  source_edition: string | null;
  synced_at: string | null;
  counts: {
    products: number | null;
    regulations: number | null;
    queue_pending: number | null;
  };
  latest_available_edition: string | null;
  is_stale: boolean;
}

export interface PesticideSyncStatus {
  /** Edition (date) of the Госкаталог dump we last applied; null = never synced. */
  sourceEdition: string | null;
  /** ISO timestamp of the last live import; null = never synced. */
  syncedAt: string | null;
  counts: {
    products: number | null;
    regulations: number | null;
    queuePending: number | null;
  };
  /** Newest edition available on the portal (best-effort; null when offline). */
  latestAvailableEdition: string | null;
  /** latestAvailableEdition > sourceEdition — a newer dump is out there. */
  isStale: boolean;
}

function toSyncStatus(r: SyncStatusResponse): PesticideSyncStatus {
  return {
    sourceEdition: r.source_edition,
    syncedAt: r.synced_at,
    counts: {
      products: r.counts?.products ?? null,
      regulations: r.counts?.regulations ?? null,
      queuePending: r.counts?.queue_pending ?? null,
    },
    latestAvailableEdition: r.latest_available_edition,
    isStale: r.is_stale,
  };
}

export async function fetchPesticideSyncStatus(
  opts: { checkLatest?: boolean } = {},
): Promise<PesticideSyncStatus> {
  // checkLatest=false skips the network probe (used by the lightweight user
  // plaque so it never waits on the portal); the admin panel passes true.
  const check = opts.checkLatest ?? true;
  const raw = await api.get<SyncStatusResponse>(
    `/api/v1/reference/pesticides/sync-status?check_latest=${check}`,
  );
  return toSyncStatus(raw);
}

export const pesticideSyncKeys = {
  status: (checkLatest: boolean) =>
    ['reference', 'pesticides', 'sync-status', checkLatest] as const,
};

export function usePesticideSyncStatus(
  opts: { checkLatest?: boolean; enabled?: boolean } = {},
): UseQueryResult<PesticideSyncStatus> {
  const checkLatest = opts.checkLatest ?? true;
  return useQuery({
    queryKey: pesticideSyncKeys.status(checkLatest),
    queryFn: () => fetchPesticideSyncStatus({ checkLatest }),
    // Freshness of a monthly-published registry does not need aggressive
    // refetch; keep it cheap and quiet.
    staleTime: 5 * 60 * 1000,
    enabled: opts.enabled ?? true,
    retry: false,
  });
}
